/**
 * Plan Spine — data fetch.
 *
 * Reads the workspace's most recent ratified-or-published `cycle_reports`
 * row. Read-only: no RPC calls, no writes, no store. Uses
 * getTypedSupabaseClient() so a drifted column name is a compile error, not
 * a silent runtime miss (see src/lib/typedSupabaseClient.ts).
 *
 * "Most recent" = newest `created_at` among rows that are `ratified` or
 * `published` for this workspace (a new row is written on every
 * ratification, so `created_at` tracks recency across a workspace's whole
 * cycle history, not just one cycle's version chain).
 *
 * Some environments carry fabricated future-dated fixture rows — rows whose
 * `created_at` is more than a week out are excluded so a fixture can never
 * masquerade as "the plan".
 */

import { getTypedSupabaseClient } from '@/lib/typedSupabaseClient';
import type { RawCycleReportRow } from './planViewModel';

const FUTURE_FIXTURE_GUARD_DAYS = 7;

function futureFixtureCutoffIso(): string {
  return new Date(Date.now() + FUTURE_FIXTURE_GUARD_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Fetches the latest published-or-ratified plan for a workspace.
 * Returns null when there is no such report, or on any read error (logged,
 * not thrown — callers render the honest empty state either way).
 */
export async function fetchLatestPlanReport(workspaceId: string): Promise<RawCycleReportRow | null> {
  const supabase = getTypedSupabaseClient();

  const { data, error } = await supabase
    .from('cycle_reports')
    .select('id, cycle_id, cycle_name, version, supersedes_id, rationale, proposal_id, amendment_log, ratified_at, ratified_by, legitimacy_state, plan_of_record')
    .eq('workspace_id', workspaceId)
    .in('legitimacy_state', ['ratified', 'published'])
    .lte('created_at', futureFixtureCutoffIso())
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[plan-spine] fetchLatestPlanReport failed:', error.message);
    return null;
  }

  return data;
}
